<script setup>
import { useConfiguratorStore } from '../../stores/configurator';
import InputField from './InputField.vue';
import Icon from '../Icon.vue';
import CheckboxField from './CheckboxField.vue';
import CountryField from './CountryField.vue';
import { reactive, ref, watch } from 'vue';
import { useSubmitStore } from '../../stores/submit.js';
import Altcha from './Altcha.vue';

const configurationStore = useConfiguratorStore();
const submitStore = useSubmitStore();

const partners = ref([]);
const partnerAddress = reactive({
    zip: null,
    country: null
});

watch(submitStore.formData, () => {
    if(submitStore.contactDealer){
        searchPartners();
    }
})

async function searchPartners() {
    const address = {
        'zip': submitStore.formData.zip,
        'country': submitStore.formData.country,
        'category': JSON.stringify(configurationStore.configuration.type)
    }

    // validate all fields
    if(!address.zip || !address.country) {
        return
    }

    // check for changes
    if(partnerAddress.zip === address.zip && partnerAddress.country === address.country) {
        return
    }

    partnerAddress.zip = address.zip;
    partnerAddress.country = address.country;

    const response = await fetch('/configurator/partner', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(address)
    })
    const json = await response.json();

    if(json.success){
        partners.value = json.data.partners;
        submitStore.activePartnerId = json.data.partners[0];
    }else{
        partners.value = [];
    }
}

</script>

<template>
    <section class="ConfiguratorContact tw-pb-8 lg:tw-pb-14">
        <div v-if="submitStore.isFinished" class="-tw-translate-y-10 lg:tw-translate-y-16">
            <div v-if="submitStore.activePartnerId !== null">
                <h2 class="tw-text-gray-700 tw-text-2xl tw-font-medium md:tw-text-40 tw-mb-4 lg:tw-mb-6" v-t="`website.configurator.contact.partner_yes.thanks`"></h2>
                <p class="tw-text-gray-700 tw-text-left tw-mb-8 lg:tw-mb-6" v-t="`website.configurator.contact.partner_yes.thanks.text`"></p>
                <a href="/de/konfigurator" class="btn primary tw-inline-block tw-text-center" v-t="`website.configurator.contact.partner_yes.thanks.link`"></a>
            </div>
            <div v-else>
                <h2 class="tw-text-gray-700 tw-text-2xl tw-font-medium md:tw-text-40 tw-mb-4 lg:tw-mb-6" v-t="`website.configurator.contact.partner_no.thanks`"></h2>
                <p class="tw-text-gray-700 tw-text-left tw-mb-8 lg:tw-mb-6" v-t="`website.configurator.contact.partner_no.thanks.text`"></p>
                <a href="/de/konfigurator" class="btn primary tw-inline-block tw-text-center" v-t="`website.configurator.contact.partner_no.thanks.link`"></a>
            </div>
        </div>

        <div
            v-if="!submitStore.isFinished"
        >
            <h2 class="tw-text-gray-700 tw-text-2xl md:tw-text-2xl lg:tw-text-3xl tw-font-semibold tw-mb-4 lg:tw-mb-6 xl:tw-mb-8 xxl:tw-mb-12">
                <span v-if="submitStore.contactDealer" v-t="`website.configurator.step.contact`"></span>
                <span v-if="!submitStore.contactDealer" v-t="`website.configurator.send_configuration`"></span>
            </h2>

            <InputField
                type="number"
                name="amountOfDoors"
                :label="$t('website.configurator.contact.amount_of_doors')"
                min="1"
                max="100"
                html5="html5"
                inputmode="decimal"
                :is-required="submitStore.contactDealer"
                v-model="submitStore.formData.amountOfDoors"
            />

            <InputField
                type="email"
                name="email"
                :label="$t('website.configurator.contact.email')"
                is-required
                v-model="submitStore.formData.email"
            />

            <CheckboxField
                :label="$t('website.configurator.contact.allowed_contact')"
                name="contact"
                v-model="submitStore.contactDealer"
            />

            <div
                class="tw-py-4 md:tw-py-6"
                v-if="submitStore.contactDealer"
            >
                <InputField
                    :label="$t('website.configurator.contact.firstname')"
                    name="firstname"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.firstname"
                />

                <InputField
                    :label="$t('website.configurator.contact.lastname')"
                    name="lastname"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.lastname"
                />

                <InputField
                    :label="$t('website.configurator.contact.phone')"
                    name="phone"
                    type="tel"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.phone"
                />

                <InputField
                    :label="$t('website.configurator.contact.zip')"
                    name="zip"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.zip"
                />

                <InputField
                    :label="$t('website.configurator.contact.city')"
                    name="city"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.city"
                />

                <CountryField
                    :label="$t('website.configurator.contact.country')"
                    name="country"
                    :is-required="submitStore.contactDealer"
                    v-model="submitStore.formData.country"
                />

                <div class="ConfiguratorContactPartner tw-my-8 md:tw-my-12 lg:tw-my-16"
                     v-if="partners.length > 0"
                >
                    <h4 class="tw-text-gray-700 tw-text-lg md:tw-text-2xl tw-font-medium tw-mb-6">
                        <span v-if="partners.length > 1" v-t="`website.configurator.contact.partner.please_choose`"></span>
                        <span v-if="partners.length === 1" v-t="`website.configurator.contact.partner.your_partner`"></span>
                    </h4>

                    <div
                        v-for="partner in partners"
                        :key="partner.id"
                        class="ConfiguratorItemPartner tw-flex tw-items-center tw-mb-2 tw-relative tw-pl-10"
                        :class="partner.id === submitStore.activePartnerId ? 'active' : ''"
                    >
                        <div class="icon">
                            <Icon name="location-pin" class="tw-size-8 tw-absolute tw-left-0 tw-top-1/2 tw-transform tw--translate-y-1/2" />
                        </div>

                        <input
                            type="radio"
                            name="contact_partner"
                            :value="partner.id"
                            v-model="submitStore.activePartnerId"
                            :id="`partner-${partner.id}`"
                            class="tw-opacity-0 tw-z-10 tw-cursor-pointer tw-absolute tw-left-0 tw-top-0 tw-w-full tw-h-full tw-mr-2"
                        >
                        <div>
                            <label
                                :for="`partner-${partner.id}`"
                                class="tw-block tw-text-gray-700 tw-text-20 tw-font-medium"
                                v-text="partner.title"
                            ></label>
                            <p
                                class="tw-text-gray-700 tw-text-base tw-font-normal"
                                v-text="`${partner.street}, ${partner.zip} ${partner.city}`">
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <CheckboxField
                :label="$t('website.configurator.contact.terms')"
                name="terms"
                is-required
                v-model="submitStore.formData.terms"
            />

            <Altcha
                v-model="submitStore.altcha"
            />
        </div>
    </section>
</template>